import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { endpoints, type CustomerListResponse } from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/**
 * The customer list and its filters (§7).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS ONE HAS A REAL SEARCH, unlike the invoice register.
 * `CustomerListQuerySchema` declares `q`, and the server matches it against
 * NAME, PHONE and GSTIN. That matters: the owner looks a customer up by
 * whichever of the three he has to hand — he remembers "Lupin", his son has the
 * GSTIN off an invoice, and a caller is identified by the number that rang.
 *
 * So the search box here is a server search over the whole book, not a filter
 * over the fetched page. Filtering the page would look identical on a short list
 * and silently miss everyone from page two onward.
 *
 * THE URL HOLDS THE STATE, for the same reasons as the invoice register: a
 * search can be linked and the back button undoes it.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** The API's default and hard ceiling — `CustomerListQuerySchema`, `.max(200)`. */
export const CUSTOMER_PAGE_SIZE = 50;

export interface CustomerFilters {
  /** Matched against name, phone AND gstin by the server. */
  q?: string;
  /** `undefined` shows archived customers too; `true` shows only current ones. */
  active?: boolean;
  offset: number;
}

export function useCustomerFilters(): {
  filters: CustomerFilters;
  setFilters: (patch: Partial<CustomerFilters>) => void;
  clearFilters: () => void;
  activeCount: number;
} {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<CustomerFilters>(() => {
    const rawActive = params.get("active");
    const rawOffset = params.get("offset");
    const q = params.get("q") ?? undefined;

    return {
      q: q === "" ? undefined : q,
      active: rawActive === null || rawActive === "" ? undefined : rawActive === "true",
      offset: rawOffset === null ? 0 : Math.max(0, Number(rawOffset) || 0),
    };
  }, [params]);

  const setFilters = useCallback(
    (patch: Partial<CustomerFilters>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === "") next.delete(key);
        else next.set(key, String(value));
      }
      // Any change but paging returns to the first page — narrowing 200 names to
      // 3 while sitting at offset 150 shows an empty page that reads as "no
      // matches" when three match.
      if (!("offset" in patch)) next.delete("offset");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const clearFilters = useCallback(() => {
    setParams(new URLSearchParams(), { replace: true });
  }, [setParams]);

  const activeCount = [filters.q, filters.active].filter((value) => value !== undefined).length;

  return { filters, setFilters, clearFilters, activeCount };
}

function toQuery(filters: CustomerFilters): Record<string, string | number | boolean | undefined> {
  return {
    q: filters.q,
    active: filters.active,
    limit: CUSTOMER_PAGE_SIZE,
    offset: filters.offset === 0 ? undefined : filters.offset,
  };
}

export function useCustomerList(filters: CustomerFilters): UseQueryResult<CustomerListResponse> {
  const query = toQuery(filters);

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();

  return useQuery({
    queryKey: queryKeys.customers.list(query),
    queryFn: () =>
      api.get<CustomerListResponse>(`${endpoints.customers}${qs === "" ? "" : `?${qs}`}`),
    // The current page stays on screen while the next loads, instead of the
    // table collapsing to a spinner and the page height jumping.
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}
